function cleanBodyText(messageBody = "") {
  return String(messageBody || "")
    .replace(/\r\n/g, "\n")
    .replace(/,\s*appears open right now/gi, "")
    .replace(/,\s*may be closed right now/gi, "")
    .replace(/\s*appears open right now,?\s*/gi, " ")
    .replace(/\s*may be closed right now,?\s*/gi, " ")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\n\s+/g, "\n")
    .trim();
}

function trimSentence(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

// Prefixing the viewer's name reads wrong when the opening word keeps its
// capital — "Jaime, Visit the site of an early internet legend" — but
// lowercasing indiscriminately would break the worse case: "Jaime, Cafe Alto
// looks worth a closer look" is correct and must stay that way. A capitalised
// word cannot be told from a proper noun by shape alone, so only a closed set of
// common sentence openers is safe to touch. Anything unrecognised keeps its
// capital, which is the behaviour we had before and is right for names.
const LOWERCASABLE_SENTENCE_OPENERS = new Set([
  "this", "that", "it", "these", "those", "there", "here",
  "a", "an", "the", "you", "your",
  "visit", "step", "head", "stop", "come", "go", "find", "discover", "expect",
  "grab", "order", "book", "walk", "bring", "skip", "picture", "imagine", "think",
  "tucked", "perched", "hidden", "set", "known", "locals", "part",
  "if", "when", "whether", "for", "with", "one", "few", "most", "every", "some",
]);

function lowerFirstWordIfGeneric(value = "") {
  const text = trimSentence(value);
  const firstWord = text.split(/\s+/)[0] || "";
  const bareWord = firstWord.replace(/[^A-Za-z]/g, "").toLowerCase();
  if (!bareWord || !LOWERCASABLE_SENTENCE_OPENERS.has(bareWord)) return text;
  return text.replace(/^[A-Za-z]/, (character) => character.toLowerCase());
}

export function splitFindCapsuleBody(messageBody = "") {
  const body = cleanBodyText(messageBody);
  const legacyMatch = body.match(/^TL;DR:\s*(.+?)(?:\n{2,}|\n|$)([\s\S]*)$/i);
  if (legacyMatch) {
    return {
      quickTakeText: trimSentence(legacyMatch[1]),
      story: String(legacyMatch[2] || "").trim(),
    };
  }

  const quickTakeMatch = body.match(/^Quick Take\s*:?\s*(.+?)(?:\n{2,}|\n|$)([\s\S]*)$/i);
  if (quickTakeMatch) {
    return {
      quickTakeText: trimSentence(quickTakeMatch[1]),
      story: String(quickTakeMatch[2] || "").trim(),
    };
  }

  const [firstParagraph = "", ...rest] = body.split(/\n{2,}/);
  return {
    quickTakeText: "",
    story: [firstParagraph, ...rest].join("\n\n").trim(),
  };
}

export function personalizeQuickTakeText(text = "", displayName = "") {
  const cleanText = trimSentence(text);
  const name = String(displayName || "").trim().split(/\s+/).filter(Boolean)[0] || "";
  if (!cleanText || !name) return cleanText;
  if (cleanText.toLowerCase().startsWith(`${name.toLowerCase()},`)) return cleanText;
  return `${name}, ${lowerFirstWordIfGeneric(cleanText)}`;
}

function normalizeSeedType(value = "") {
  return String(value || "").trim().toLowerCase();
}

function normalizeCapsuleType(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[-_\s]/g, "");
}

const FIND_SEED_TYPES = new Set(["see", "eat", "drink", "do", "shop", "relax", "discover"]);

function isFindGeneratedCapsule(capsule = {}) {
  if (!capsule || typeof capsule !== "object") return false;
  if (capsule.seeded !== true) return false;
  if (normalizeCapsuleType(capsule.capsuleType) !== "public") return false;
  return FIND_SEED_TYPES.has(normalizeSeedType(capsule.seedType));
}

export function getLegacyFindCapsuleTextUpgrade(capsule = {}) {
  if (!isFindGeneratedCapsule(capsule)) return null;

  const originalBody = String(capsule?.messageBody || "");
  const cleanedBody = originalBody
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (!/^tl;dr\s*:/i.test(cleanedBody)) return null;

  const messageBody = cleanedBody.replace(/^tl;dr\s*:/i, "Quick Take:").trim();
  if (!messageBody || messageBody === originalBody.trim()) return null;
  return { messageBody };
}

function categoryGoodFor(seedType = "", placeFacts = {}) {
  const typeList = Array.isArray(placeFacts?.types)
    ? placeFacts.types.map((type) => String(type || "").toLowerCase())
    : [];
  const seed = normalizeSeedType(seedType);
  if (seed === "eat") return "casual food, quick plans, easy meetups";
  if (seed === "drink") return "drinks, resets, social plans";
  if (seed === "do") return "things to do, flexible plans, nearby fun";
  if (seed === "shop") return "browsing, errands, local finds";
  if (seed === "relax") return "slowing down, wellness, quieter moments";
  if (seed === "see") return "sights, local stories, low-key exploring";
  if (typeList.some((type) => /restaurant|cafe|bakery|meal|food/.test(type))) {
    return "casual food, quick plans, easy meetups";
  }
  if (typeList.some((type) => /bar|night_club|brewery/.test(type))) {
    return "drinks, resets, social plans";
  }
  if (typeList.some((type) => /store|shop|mall/.test(type))) {
    return "browsing, errands, local finds";
  }
  if (typeList.some((type) => /park|museum|tourist_attraction|landmark/.test(type))) {
    return "sights, local stories, low-key exploring";
  }
  return "nearby plans, local discovery, easy decisions";
}

function formatReviewCount(value) {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return "";
  return Math.round(count).toLocaleString();
}

function buildQuickTakeBullets({ seedType = "", placeFacts = {} } = {}) {
  const facts = placeFacts && typeof placeFacts === "object" ? placeFacts : {};
  const rating = Number(facts.rating);
  const reviewCount = formatReviewCount(facts.reviewCount);
  const priceLevel = Number(facts.priceLevel);
  const bullets = [
    { label: "Good for", value: categoryGoodFor(seedType, facts) },
  ];

  if (Number.isFinite(priceLevel) && priceLevel > 0) {
    bullets.push({ label: "Price", value: "$".repeat(Math.min(priceLevel, 4)) });
  }

  if (Number.isFinite(rating) && rating > 0) {
    bullets.push({
      label: "Why it stands out",
      value: `${rating.toFixed(1)}-star rating${reviewCount ? ` with ${reviewCount} reviews` : ""}`,
    });
  }

  return bullets.filter((item) => item.value);
}

// Gemini separates paragraphs inconsistently — sometimes a blank line, sometimes
// a single newline. Rendering the raw string with pre-wrap gave the single-newline
// case no visible break at all, so the story ran together. Splitting on any run of
// newlines lets the page space paragraphs itself rather than depending on how the
// model happened to format that particular body.
export function splitStoryParagraphs(story = "") {
  return String(story || "")
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function buildFindQuickTake({
  messageBody = "",
  displayName = "",
  seedType = "",
  placeFacts = {},
} = {}) {
  const bodyParts = splitFindCapsuleBody(messageBody);
  return {
    title: "Quick Take",
    quickTakeText: personalizeQuickTakeText(bodyParts.quickTakeText, displayName),
    story: bodyParts.story,
    storyParagraphs: splitStoryParagraphs(bodyParts.story),
    bullets: buildQuickTakeBullets({ seedType, placeFacts }),
  };
}
